import { Lightbulb, Map as MapIcon, ScrollText, Star } from 'lucide-react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCurrentPurpose } from '@/store/useBookAiPurposeStore'

import { KeyPointsTab } from './key-points-tab'
import { PracticePanel } from './practice-panel'
import { PurposeTab } from './purpose-tab'
import { ThesisTab } from './thesis-tab'
import type { BookAiReaderState } from './useBookAi'

export type BookOverviewView = 'plan' | 'points' | 'thesis' | 'practice'

const VIEWS: { value: BookOverviewView; label: string; icon: typeof Star }[] = [
  { value: 'plan', label: '読み方', icon: MapIcon },
  { value: 'points', label: '重要箇所', icon: Star },
  { value: 'thesis', label: '本の主張', icon: ScrollText },
  { value: 'practice', label: '実践メモ', icon: Lightbulb },
]

const isView = (value: string): value is BookOverviewView => VIEWS.some((view) => view.value === value)

type Props = {
  bookAi: BookAiReaderState
  view: BookOverviewView
  onViewChange: (view: BookOverviewView) => void
  onNavigate: (key: number) => void
}

/** 本の全体: 読み方（目的に合わせた章の読み方）・重要箇所・本の主張・実践メモを切り替えて見る */
export const BookOverviewTab = ({ bookAi, view, onViewChange, onNavigate }: Props) => {
  const purpose = useCurrentPurpose(bookAi.episodeId)
  return (
    <Tabs value={view} onValueChange={(value) => isView(value) && onViewChange(value)} className="gap-3">
      <TabsList className="h-8 w-full bg-transparent p-0">
        {VIEWS.map(({ value, label, icon: Icon }) => (
          <TabsTrigger
            key={value}
            value={value}
            className="rounded-full border-border px-2 text-xs data-[state=active]:border-primary/50 dark:data-[state=active]:border-primary/50"
          >
            <Icon className="size-3.5" />
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="plan">
        <PurposeTab bookAi={bookAi} onNavigate={onNavigate} />
      </TabsContent>
      <TabsContent value="points">
        <KeyPointsTab bookAi={bookAi} onNavigate={onNavigate} />
      </TabsContent>
      <TabsContent value="thesis">
        <ThesisTab thesis={bookAi.status?.thesis ?? null} sections={bookAi.sections} onNavigate={onNavigate} />
      </TabsContent>
      <TabsContent value="practice">
        {purpose?.map ? (
          <PracticePanel bookAi={bookAi} practice={purpose.practice} onNavigate={onNavigate} />
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {purpose ? 'AI 解析が終わると実践メモを作れます' : '読む目的を決めると、目的に対する実践メモを作れます'}
          </p>
        )}
      </TabsContent>
    </Tabs>
  )
}
